import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import reportService, { ReportNotFoundError } from '../../services/api/reportService';
import { transformReportData } from '../../components/report/transformReportData';
import ReportHeader from '../../components/report/ReportHeader';
import SlidePresentation from '../../components/report/SlidePresentation';
import CoverSlide from '../../components/report/slides/CoverSlide';
import TOCSlide from '../../components/report/slides/TOCSlide';
import OverviewSlide from '../../components/report/slides/OverviewSlide';
import USPDetailSlide from '../../components/report/slides/USPDetailSlide';
import SegmentsSlide from '../../components/report/slides/SegmentsSlide';
import SegmentFocusSlide from '../../components/report/slides/SegmentFocusSlide';
import InsightSlide from '../../components/report/slides/InsightSlide';
import StrategySlide from '../../components/report/slides/StrategySlide';
import BannerGridSlide from '../../components/report/slides/BannerGridSlide';
import BannerVariationSlide from '../../components/report/slides/BannerVariationSlide';
import CTASlide from '../../components/report/slides/CTASlide';
import '../../styles/report-slides.css';

const POLLING_INTERVAL_MS = 5000;
const MAX_POLLING_COUNT = 60; // 5 minutes max

/**
 * ReportPage - Display analysis report as a slide presentation.
 * Accessible at /report/:slug (public route, no auth required).
 * Polls for report status if pending/processing.
 * Shows SlidePresentation when report is completed.
 */
const ReportPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);

  const [report, setReport] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pollingCount, setPollingCount] = useState(0);

  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchReport = useCallback(async () => {
    if (!slug) return;

    try {
      const data = await reportService.getBySlug(slug);
      setReport(data);
      setLoading(false);

      // Stop polling if report is completed or failed
      if (data.report_status === 'completed' || data.report_status === 'failed') {
        if (pollingRef.current) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
        }
      }
    } catch (err: any) {
      if (err instanceof ReportNotFoundError || err?.response?.status === 404) {
        setError('レポートが見つかりません。');
      } else {
        setError('レポートの取得に失敗しました。');
      }
      setLoading(false);
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    }
  }, [slug]);

  // Initial fetch and polling setup
  useEffect(() => {
    if (!slug) {
      setError('レポートのスラッグが指定されていません。');
      setLoading(false);
      return;
    }

    fetchReport();

    // Start polling
    pollingRef.current = setInterval(() => {
      setPollingCount((prev) => {
        const next = prev + 1;
        if (next >= MAX_POLLING_COUNT) {
          if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
          }
          setError('レポートの生成がタイムアウトしました。しばらくしてから再度アクセスしてください。');
          return next;
        }
        fetchReport();
        return next;
      });
    }, POLLING_INTERVAL_MS);

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [slug, fetchReport]);

  // Stop polling when report is completed or failed
  useEffect(() => {
    if (
      report &&
      (report.report_status === 'completed' || report.report_status === 'failed') &&
      pollingRef.current
    ) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, [report]);

  // --- Loading State ---
  if (loading) {
    return (
      <div className="min-h-screen bg-[#f6f5f0]">
        <ReportHeader isLoggedIn={isAuthenticated} />
        <div className="flex items-center justify-center" style={{ minHeight: 'calc(100vh - 64px)' }}>
          <div className="text-center">
            <div className="flex justify-center">
              <svg
                className="animate-spin w-8 h-8 text-[#3eb298]"
                viewBox="0 0 24 24"
              >
                <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" fill="none" />
                <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            </div>
            <p className="mt-4 text-[16px] text-[#1d1926]/60">
              レポートを読み込み中...
            </p>
          </div>
        </div>
      </div>
    );
  }

  // --- Error State ---
  if (error) {
    return (
      <div className="min-h-screen bg-[#f6f5f0]">
        <ReportHeader isLoggedIn={isAuthenticated} />
        <div className="flex items-center justify-center" style={{ minHeight: 'calc(100vh - 64px)' }}>
          <div className="text-center max-w-[480px] px-6">
            <div className="w-16 h-16 mx-auto rounded-full bg-red-500/10 flex items-center justify-center">
              <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h2 className="mt-4 text-[20px] font-bold text-[#1d1926]">
              エラー
            </h2>
            <p className="mt-2 text-[15px] text-[#1d1926]/60">{error}</p>
            <button
              onClick={() => navigate('/diagnose')}
              className="mt-6 px-8 py-3 rounded-full bg-[#3eb298] text-white text-[15px] font-semibold border-none cursor-pointer hover:opacity-90 transition-opacity"
            >
              もう一度分析する
            </button>
          </div>
        </div>
      </div>
    );
  }

  // --- Pending / Processing State ---
  if (report && (report.report_status === 'pending' || report.report_status === 'processing')) {
    return (
      <div className="min-h-screen bg-[#f6f5f0]">
        <ReportHeader isLoggedIn={isAuthenticated} />
        <main className="max-w-[1000px] mx-auto px-6 py-16 text-center">
          <div className="w-16 h-16 mx-auto rounded-full bg-[#3eb298]/10 flex items-center justify-center mb-6">
            <svg className="animate-spin h-6 w-6 text-[#3eb298]" viewBox="0 0 24 24">
              <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" fill="none" />
              <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          </div>
          <h2 className="text-[24px] font-bold text-[#1d1926]">
            レポートを作成中です...
          </h2>
          <p className="mt-2 text-[15px] text-[#1d1926]/60">
            数分以内に完成します。このページを開いたままお待ちください。
          </p>
        </main>
      </div>
    );
  }

  // --- Failed State ---
  if (report && report.report_status === 'failed') {
    return (
      <div className="min-h-screen bg-[#f6f5f0]">
        <ReportHeader isLoggedIn={isAuthenticated} />
        <div className="flex items-center justify-center" style={{ minHeight: 'calc(100vh - 64px)' }}>
          <div className="text-center max-w-[480px] px-6">
            <div className="w-16 h-16 mx-auto rounded-full bg-red-500/10 flex items-center justify-center">
              <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="mt-4 text-[20px] font-bold text-[#1d1926]">
              レポートの生成に失敗しました
            </h2>
            <p className="mt-2 text-[15px] text-[#1d1926]/60">
              申し訳ございません。もう一度お試しください。
            </p>
            <button
              onClick={() => navigate('/diagnose')}
              className="mt-6 px-8 py-3 rounded-full bg-[#3eb298] text-white text-[15px] font-semibold border-none cursor-pointer hover:opacity-90 transition-opacity"
            >
              もう一度分析する
            </button>
          </div>
        </div>
      </div>
    );
  }

  // --- Completed State: Slide Presentation ---
  if (!report) return null;

  const transformed = transformReportData(report);
  const segments = transformed.segments ?? [];
  const strategies = transformed.appeal_strategies ?? [];

  // Build labels for bottom nav
  const labels: string[] = [
    'Cover',
    '目次',
    '全体像',
    'Step 01',
    '自社の強み',
    'Step 02',
    '3つの相手',
    `${segments[0]?.name || 'セグメント1'}`,
    'インサイト',
    'WHAT Focus',
    '伝え方',
    'バナー一覧',
  ];

  // Add variation labels for each strategy
  strategies.slice(0, 3).forEach((strat) => {
    labels.push(strat.name || 'バリエーション');
  });

  labels.push('全体像(2)');
  labels.push('Unlock');

  // Build slideStyles for special backgrounds
  const slideStyles: Record<number, React.CSSProperties> = {
    0: { background: '#F6F5F0', color: '#1D1926' },
  };

  // CTA slide (last one) has dark background
  const ctaSlideIndex = labels.length - 1;
  slideStyles[ctaSlideIndex] = { background: '#1D1926', color: '#fff' };

  // Build slideClassNames
  const slideClassNames: Record<number, string> = {
    0: 'slide-cover',
    [ctaSlideIndex]: 'slide-cta',
  };

  // Build slide components
  const slides: React.ReactNode[] = [
    <CoverSlide key="cover" data={transformed} />,
    <TOCSlide key="toc" />,
    <OverviewSlide key="ov-all" data={transformed} highlightColumn="all" />,
    <OverviewSlide key="ov-usp" data={transformed} highlightColumn="usp" />,
    <USPDetailSlide key="usp-detail" data={transformed} />,
    <OverviewSlide key="ov-who" data={transformed} highlightColumn="who" />,
    <SegmentsSlide key="segments" data={transformed} />,
    <SegmentFocusSlide key="seg-focus" data={transformed} focusIndex={0} />,
    <InsightSlide key="insight" data={transformed} segmentIndex={0} />,
    <OverviewSlide key="ov-iw" data={transformed} highlightColumn="insight-what" highlightSegmentRow={0} />,
    <StrategySlide key="strategy" data={transformed} segmentIndex={0} />,
    <BannerGridSlide key="banner-grid" data={transformed} />,
  ];

  // Banner variation slides (one per strategy, up to 3)
  strategies.slice(0, 3).forEach((_, si) => {
    slides.push(
      <BannerVariationSlide key={`banner-var-${si}`} data={transformed} strategyIndex={si} />
    );
  });

  slides.push(
    <OverviewSlide key="ov-lock" data={transformed} highlightColumn="all" showLockOverlay={true} />,
    <CTASlide key="cta" />
  );

  return (
    <SlidePresentation
      labels={labels}
      slideStyles={slideStyles}
      slideClassNames={slideClassNames}
    >
      {slides}
    </SlidePresentation>
  );
};

export default ReportPage;
